import { Modal } from 'react-bootstrap';
import { t } from 'react-switch-lang';
import { events, logAmpEvent } from '../utils/Amplitude';
import styles from '../styles/ConfirmationModal.module.scss';
import Button from './Button';
import { moveFocusTo, setScreenReaderAlert } from '../utils/Accessibility';
import { formatAsTestId } from '../utils/Helpers';

/**
 * Renders a confirmation modal with static message.
 *
 * @param {object} props
 * @param {boolean} [props.showModal=false] Flag to determine if the modal should be displayed
 * @param {function} props.onCancel Callback function to execute when cancel button is clicked
 * @param {function} props.onConfirm Callback function to execute when confirm button is clicked
 * @param {string} props.ampEventPropsPageName Page name for the AMP event props
 * @param {string} props.stepName Step name to be used in cancel button aria-label to notify
 * the user which step they will stay on if they cancel
 * @param {string} [props.returnFocusTo] ID of the element to return focus to when the modal closes,
 * If not provided, focus will return to the focus-helper element which is at the top of the page
 */
export default function ConfirmationModal({
  showModal = false,
  onCancel,
  onConfirm,
  ampEventPropsPageName,
  stepName,
  returnFocusTo = 'focus-helper',
}) {
  const handleScreenReaderAlert = () => {
    setScreenReaderAlert(`${t('Confirmation_Modal_Warning_Title')} ${t('Confirmation_Modal_Warning_Text')}`);
  };

  const handleCancel = () => {
    logAmpEvent(events.USER_CLOSED_CONFIRMATION_MODAL, { Page: ampEventPropsPageName });
    setScreenReaderAlert(t('Confirmation_Modal_Closed_Alert'));
    onCancel();
    moveFocusTo(`#${returnFocusTo}`, undefined, true);
  };

  return (
    <Modal
      centered
      show={showModal}
      animation={false}
      onHide={handleCancel}
      onShow={handleScreenReaderAlert}
      dialogClassName={styles.confirmationModal}
    >
      <Modal.Body className={styles.confirmationModalBody}>
        <h1>{t('Confirmation_Modal_Warning_Title')}</h1>
        {t('Confirmation_Modal_Warning_Text')}
      </Modal.Body>
      <Modal.Footer className={styles.confirmationModalFooter}>
        <Button
          className={styles.cancelButton}
          text={t('Confirmation_Modal_Cancel_Button')}
          ariaLabel={t('Confirmation_Modal_Cancel_ButtonDescription').replace('xStep', stepName)}
          onClick={handleCancel}
          testId={formatAsTestId('confirmation modal cancel', 'button')}
        />
        <Button
          text={t('Confirmation_Modal_GoBack_ButtonText')}
          ariaLabel={t('Confirmation_Modal_GoBack_ButtonDescription')}
          ampEvent={events.USER_CLICKED_CONFIRMATION_MODAL_GO_BACK_BUTTON}
          ampEventProps={{ Page: ampEventPropsPageName }}
          onClick={onConfirm}
          testId={formatAsTestId('confirmation modal go back', 'button')}
        />
      </Modal.Footer>
    </Modal>
  );
}
